import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './SubscriptionsSection.css';
import {
  FiArrowUpRight,
  FiAward,
  FiCheck,
  FiShield,
  FiStar,
  FiTrendingUp,
  FiUsers,
  FiX,
  FiZap,
} from 'react-icons/fi';
import { listMembershipPlans } from '../../../services/membershipsApi';
import { setCompanyMembershipPlan } from '../../../services/companyApi';
import { createCancellationReason } from '../../../services/cancellationReasonsApi';
import { useNavigate } from 'react-router-dom';

const PRICE_FORMATTER = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatPrice = value => {
  if (value === null || value === undefined) return 'Gratis';
  if (Number(value) === 0) return 'Gratis';
  return PRICE_FORMATTER.format(Number(value));
};

const chunkPlans = (plans, size) => {
  const chunks = [];
  for (let i = 0; i < plans.length; i += size) {
    chunks.push(plans.slice(i, i + size));
  }
  return chunks;
};

const HERO_CARD_GRADIENT = 'linear-gradient(135deg, #0f172a 0%, #1c2535 55%, #1f2937 100%)';
const HERO_CARD_SHADOW = '0 18px 35px -25px rgba(15, 23, 42, 0.28)';

const HERO_THEMES = {
  elite: {
    badge: 'FLOTTE ELITE',
    label: 'Elite',
    subtitle: 'Estado prioritario, visibilidad total y concierge dedicado.',
    background: HERO_CARD_GRADIENT,
    accent: '#f6cf74',
    accentSoft: 'rgba(246, 207, 116, 0.28)',
    glow: HERO_CARD_SHADOW,
  },
  premium: {
    badge: 'FLOTTE PREMIUM',
    label: 'Premium',
    subtitle: 'Mayor exposición, insights avanzados y soporte prioritario.',
    background: HERO_CARD_GRADIENT,
    accent: '#9fe2ff',
    accentSoft: 'rgba(159, 226, 255, 0.32)',
    glow: HERO_CARD_SHADOW,
  },
  pro: {
    badge: 'FLOTTE PRO',
    label: 'Pro',
    subtitle: 'Herramientas esenciales para dominar tus operaciones.',
    background: HERO_CARD_GRADIENT,
    accent: '#93c5fd',
    accentSoft: 'rgba(147, 197, 253, 0.32)',
    glow: HERO_CARD_SHADOW,
  },
  default: {
    badge: 'FLOTTE MEMBER',
    label: 'Membresia',
    subtitle: 'Administra y celebra tu relacion con Flotte.',
    background: HERO_CARD_GRADIENT,
    accent: '#cbd5f5',
    accentSoft: 'rgba(203, 213, 245, 0.28)',
    glow: HERO_CARD_SHADOW,
  },
};

const HERO_PLAN_LABELS = {
  elite: 'Elite',
  premium: 'Premium',
  pro: 'Pro',
  founder: 'Founder',
  free: 'Free',
};

const POWER_UP_ICONS = [FiShield, FiTrendingUp, FiZap, FiAward];

const CANCELLATION_REASONS = [
  { value: 'cost', label: 'Es muy costoso' },
  { value: 'usage', label: 'No lo uso suficiente' },
  { value: 'alternative', label: 'Encontré otra opción' },
];

const RETENTION_STEP_TITLES = [
  '¿Seguro que quieres renunciar a estos beneficios?',
  'Ayúdanos a mejorar',
  'Confirmar cancelación',
];

const buildPowerUps = planLabel => [
  {
    key: 'vip-support',
    icon: FiShield,
    title: 'Soporte VIP',
    description: `Concierge ${planLabel} con respuesta prioritaria y seguimiento personalizado.`,
  },
  {
    key: 'visibility',
    icon: FiTrendingUp,
    title: 'Impulso de visibilidad',
    description: 'Tus publicaciones aparecen destacadas en el feed y en el marketplace.',
  },
  {
    key: 'launchpad',
    icon: FiZap,
    title: 'Boost de campañas',
    description: 'Créditos incluidos para activar campañas PPC y captar leads calificados.',
  },
  {
    key: 'circle',
    icon: FiAward,
    title: 'Círculo Elite',
    description: 'Acceso exclusivo a betas, workshops y enlaces con el equipo Flotte.',
  },
];

const DATE_FORMATTER = new Intl.DateTimeFormat('es-MX', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

const formatMemberSinceYear = value => {
  if (!value) return '—';
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return String(date.getFullYear());
  }
  const match = String(value).match(/(20\d{2}|19\d{2})/);
  return match ? match[0] : '—';
};

const formatRenewalDate = value => {
  if (!value) return 'Sin fecha';
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return DATE_FORMATTER.format(date);
  }
  return String(value);
};

export default function SubscriptionsSection({
  empresaId = '',
  empresaName = '',
  currentPlanId,
  publicationLimit = 0,
  userLimit = 0,
  ppcCredits = null,
  leadCredits = null,
  canPublish = false,
  isVerified = false,
  onSelectPlan,
  onRequestSupport,
  onClose,
  onlySlugs = null,
  variant = 'page',
  memberSince = '',
  publicationsUsed = 0,
  teamSize = 0,
  membershipRenewsAt = '',
}) {
  const isModal = variant === 'modal';
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [isEntering, setIsEntering] = useState(() => typeof window === 'undefined');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelStep, setCancelStep] = useState(0);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelFeedback, setCancelFeedback] = useState('');
  const [cancelSaving, setCancelSaving] = useState(false);
  const [cancelError, setCancelError] = useState('');

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const fetched = await listMembershipPlans();
        if (!ignore) {
          setPlans(fetched);
        }
      } catch (err) {
        if (!ignore) {
          setError(err?.message || 'No fue posible obtener los planes de membresia.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      setIsEntering(true);
      return;
    }
    const frame = window.requestAnimationFrame(() => setIsEntering(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  const findPlanById = useCallback(
    id => plans.find(plan => plan.slug === id || plan.id === id) || null,
    [plans]
  );

  const inferPlanIdFromLimit = useCallback(() => {
    if (!plans.length) return null;
    if (!Number.isFinite(Number(publicationLimit)) || Number(publicationLimit) <= 0) {
      return plans[0].slug;
    }
    const sorted = [...plans].sort(
      (a, b) => (Number(a.publicationsLimit) || 0) - (Number(b.publicationsLimit) || 0)
    );
    const match = sorted.find(
      plan => Number(publicationLimit) <= (Number(plan.publicationsLimit) || 0)
    );
    return (match || sorted.at(-1)).slug;
  }, [plans, publicationLimit]);

  const normalizedPlanId = useMemo(() => {
    if (currentPlanId) {
      const fromCompany = findPlanById(currentPlanId);
      if (fromCompany) {
        return fromCompany.slug;
      }
    }
    return inferPlanIdFromLimit();
  }, [currentPlanId, findPlanById, inferPlanIdFromLimit]);

  const plansWithPrices = useMemo(() => {
    return plans.map(plan => {
      const priceValue =
        billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
      return {
        ...plan,
        displayPrice: formatPrice(priceValue),
        priceLabel:
          priceValue === null || Number(priceValue) === 0
            ? 'Plan gratuito'
            : billingCycle === 'monthly'
            ? 'al mes'
            : 'al ano',
        features: Array.isArray(plan.features) ? plan.features : [],
      };
    });
  }, [plans, billingCycle]);

  const displayPlans = useMemo(() => {
    if (Array.isArray(onlySlugs) && onlySlugs.length) {
      const allowed = onlySlugs.map(slug => String(slug).toLowerCase());
      return plansWithPrices.filter(plan => allowed.includes(plan.slug.toLowerCase()));
    }
    return plansWithPrices;
  }, [plansWithPrices, onlySlugs]);

  const planGroups = useMemo(() => {
    if (!displayPlans.length) return [];
    if (isModal) {
      return [displayPlans];
    }
    return chunkPlans(displayPlans, 3);
  }, [displayPlans, isModal]);

  const activePlan = useMemo(() => {
    const selected = findPlanById(normalizedPlanId);
    if (selected) return selected;
    return plansWithPrices[0] || null;
  }, [findPlanById, normalizedPlanId, plansWithPrices]);

  const effectivePublicationLimit =
    Number(publicationLimit) || (activePlan ? Number(activePlan.publicationsLimit) : 0);
  const effectiveUserLimit =
    Number(userLimit) || (activePlan ? Number(activePlan.teamSeats) : 0);
  const effectivePpcCredits =
    (ppcCredits ?? (activePlan ? Number(activePlan.ppcCredits) : 0)) || 0;
  const effectiveLeadCredits =
    (leadCredits ?? (activePlan ? Number(activePlan.leadCredits) : 0)) || 0;

  const membershipSlug = (activePlan?.slug || normalizedPlanId || currentPlanId || '').toLowerCase();
  const heroTheme = HERO_THEMES[membershipSlug] || HERO_THEMES.default;
  const heroPlanLabel = activePlan?.name || HERO_PLAN_LABELS[membershipSlug] || heroTheme.label;
  const membershipThemeStyles = useMemo(
    () => ({
      '--hero-accent': heroTheme.accent,
      '--hero-accent-soft': heroTheme.accentSoft,
    }),
    [heroTheme]
  );
  const heroBadgeLabel = useMemo(() => {
    const badge = heroTheme.badge || heroPlanLabel || 'Member';
    return badge.replace(/flotte\s+/i, '').trim().toUpperCase();
  }, [heroPlanLabel, heroTheme.badge]);

  const heroCardStyles = useMemo(
    () => ({
      '--hero-card-bg': heroTheme.background,
      '--hero-card-shadow': heroTheme.glow,
    }),
    [heroTheme]
  );

  const memberSinceYear = useMemo(() => formatMemberSinceYear(memberSince), [memberSince]);
  const showMemberSince = memberSinceYear && memberSinceYear !== '—';
  const basePowerUps = useMemo(() => buildPowerUps(heroPlanLabel), [heroPlanLabel]);
  const benefitTone = useCallback(title => {
    const normalized = String(title || '').toLowerCase();
    if (normalized.includes('soporte') || normalized.includes('vip')) {
      return 'blue';
    }
    if (normalized.includes('verificacion') || normalized.includes('acceso')) {
      return 'amber';
    }
    return 'neutral';
  }, []);

  const powerUps = useMemo(() => {
    if (!activePlan?.features?.length) {
      return basePowerUps;
    }
    const extras = activePlan.features.slice(0, 2).map((feature, index) => {
      const Icon = POWER_UP_ICONS[(index + basePowerUps.length) % POWER_UP_ICONS.length];
      const trimmed = String(feature).trim();
      const needsFallback = trimmed.length > 52;
      return {
        key: `feature-${index}`,
        icon: Icon,
        title: needsFallback ? `Beneficio ${index + 1}` : trimmed,
        description: needsFallback ? trimmed : 'Incluido en tu membresia.',
      };
    });
    return [...basePowerUps, ...extras];
  }, [activePlan?.features, basePowerUps]);

  const publicationsUsedValue = Math.max(0, Number(publicationsUsed) || 0);
  const publicationLimitValue = effectivePublicationLimit > 0 ? Number(effectivePublicationLimit) : 0;
  const publicationsHelper = publicationLimitValue
    ? `${Math.max(publicationLimitValue - publicationsUsedValue, 0)} disponibles`
    : 'Sin limite definido';

  const availableCredits = Number(effectivePpcCredits || 0) + Number(effectiveLeadCredits || 0);
  const teamSeatLimit = Number(effectiveUserLimit) || 0;

  const usageLabel = publicationLimitValue
    ? `${publicationsUsedValue}/${publicationLimitValue}`
    : `${publicationsUsedValue}`;

  const teamDisplayLabel = `${teamSize || 0} miembros activos`;

  const creditsBalanceValue = formatPrice(Number(availableCredits) || 0);

  const renewalLabel = formatRenewalDate(membershipRenewsAt);
  const statCards = useMemo(
    () => [
      {
        key: 'publications',
        icon: FiTrendingUp,
        value: usageLabel,
        label: 'Publicaciones activas',
        helper: publicationsHelper,
      },
      {
        key: 'credits',
        icon: FiZap,
        value: creditsBalanceValue,
        label: 'Créditos combinados',
        helper: 'PPC + Leads disponibles',
      },
      {
        key: 'team',
        icon: FiUsers,
        value: teamSeatLimit ? `${Math.max(teamSize || 0, 0)}/${teamSeatLimit}` : `${Math.max(teamSize || 0, 0)}`,
        label: 'Miembros del equipo',
        helper: teamDisplayLabel,
      },
      {
        key: 'plan',
        icon: FiShield,
        value: heroPlanLabel,
        label: 'Plan activo',
        helper: `Renueva el ${renewalLabel}`,
        badge: canPublish ? 'Activo' : 'Revisar acceso',
        badgeTone: canPublish ? 'success' : 'warning',
        cancelAction: true,
      },
    ],
    [
      creditsBalanceValue,
      heroPlanLabel,
      publicationsHelper,
      renewalLabel,
      teamDisplayLabel,
      teamSeatLimit,
      teamSize,
      usageLabel,
      canPublish,
    ]
  );

  const retentionLosses = useMemo(
    () => [
      {
        key: 'support',
        title: 'Soporte VIP',
        description: 'Tu concierge dedicado dejará de priorizar tus tickets.',
        icon: FiShield,
        tone: 'blue',
      },
      {
        key: 'visibility',
        title: 'Visibilidad destacada',
        description: 'Tus campañas y publicaciones perderán impulso inmediato.',
        icon: FiTrendingUp,
        tone: 'amber',
      },
      {
        key: 'verification',
        title: 'Verificación',
        description: 'Tu badge de empresa verificada podría desactivarse.',
        icon: FiStar,
        tone: 'amber',
      },
    ],
    []
  );

  const freePlanSnapshot = useMemo(() => {
    const match = plans.find(
      plan => String(plan.slug || plan.id || '').trim().toLowerCase() === 'free'
    );
    if (match) return match;
    return null;
  }, [plans]);

  const cancellationContext = useMemo(
    () => ({
      planId: activePlan?.id || normalizedPlanId || '',
      planSlug: activePlan?.slug || normalizedPlanId || '',
      planName: activePlan?.name || '',
      billingCycle,
      membershipRenewsAt,
      memberSince,
      company: empresaName,
      credits: {
        ppc: effectivePpcCredits,
        lead: effectiveLeadCredits,
      },
      publications: {
        limit: effectivePublicationLimit,
        used: publicationsUsedValue,
      },
      team: {
        size: teamSize,
        limit: effectiveUserLimit,
      },
    }),
    [
      activePlan?.id,
      activePlan?.name,
      activePlan?.slug,
      billingCycle,
      effectiveLeadCredits,
      effectivePublicationLimit,
      effectivePpcCredits,
      effectiveUserLimit,
      empresaName,
      membershipRenewsAt,
      memberSince,
      normalizedPlanId,
      publicationsUsedValue,
      teamSize,
    ]
  );

  const notifyPlanDowngrade = useCallback(() => {
    if (typeof window === 'undefined') return;
    const timestamp = new Date().toISOString();
    const snapshot =
      freePlanSnapshot || {
        id: 'free',
        slug: 'free',
        name: 'Free',
        publicationsLimit: 0,
        teamSeats: 0,
        ppcCredits: 0,
        leadCredits: 0,
      };
    const detail = {
      planId: snapshot.slug || snapshot.id || 'free',
      planName: snapshot.name || 'Free',
      billingCycle: 'monthly',
      timestamp,
      planSnapshot: snapshot,
    };
    window.dispatchEvent(new CustomEvent('flotte:plan-updated', { detail }));
  }, [freePlanSnapshot]);


  const modalCopy = useMemo(() => {
    if (!isModal) return null;
    const slug = (activePlan?.slug || '').toLowerCase();
    if (slug === 'founder') {
      return {
        ribbon: 'Plan actual: Founder',
        title: 'Desbloquea el siguiente nivel',
        subtitle:
          'Actualiza para obtener mas creditos, reportes y visibilidad en el marketplace.',
      };
    }
    return {
      ribbon: 'Plan actual: Free',
      title: 'Actualiza tu plan',
      subtitle:
        'Impulsa tus publicaciones con mas beneficios y herramientas pensadas para empresas en crecimiento.',
    };
  }, [isModal, activePlan]);

  const handlePlanAction = useCallback(
    plan => {
      if (!plan || plan.slug === normalizedPlanId) return;
      if (typeof onSelectPlan === 'function') {
        onSelectPlan(plan, billingCycle);
        return;
      }
      if (typeof onRequestSupport === 'function') {
        onRequestSupport(plan);
        return;
      }
      if (typeof onClose === 'function') {
        onClose();
      }
      navigate(`/checkout/plan/${plan.slug}`, {
        state: {
          plan,
          billingCycle,
          companyId: empresaId,
          companyName: empresaName,
          currentPlan: normalizedPlanId,
        },
      });
    },
    [
      billingCycle,
      empresaId,
      empresaName,
      navigate,
      normalizedPlanId,
      onClose,
      onRequestSupport,
      onSelectPlan,
    ]
  );

  const handleManageSubscription = useCallback(() => {
    if (typeof onRequestSupport === 'function') {
      onRequestSupport(activePlan || null);
      return;
    }
    navigate('/dashboard?section=support-new');
  }, [activePlan, navigate, onRequestSupport]);

  const handleContactSales = useCallback(() => {
    if (typeof onRequestSupport === 'function') {
      onRequestSupport({ slug: 'enterprise' });
      return;
    }
    navigate('/dashboard?section=support-new');
  }, [navigate, onRequestSupport]);

  const handleOpenCancelModal = useCallback(() => {
    setCancelStep(0);
    setCancelReason('');
    setCancelFeedback('');
    setCancelError('');
    setCancelSaving(false);
    setShowCancelModal(true);
  }, []);

  const handleCloseCancelModal = useCallback(() => {
    setShowCancelModal(false);
    setCancelStep(0);
    setCancelReason('');
    setCancelFeedback('');
    setCancelError('');
    setCancelSaving(false);
  }, []);

  const handleAdvanceCancelStep = useCallback(() => {
    setCancelError('');
    setCancelStep(prev => Math.min(prev + 1, 2));
  }, []);

  const handleBackCancelStep = useCallback(() => {
    setCancelError('');
    setCancelStep(prev => Math.max(prev - 1, 0));
  }, []);

  const handleConfirmCancel = useCallback(async () => {
    if (cancelSaving) return;
    setCancelError('');
    setCancelSaving(true);

    const subscriptionId = activePlan?.id || normalizedPlanId || currentPlanId || '';

    try {
      await createCancellationReason({
        reason: cancelReason || 'other',
        notes: cancelFeedback,
        subscriptionId,
        context: cancellationContext,
      });

      if (empresaId) {
        await setCompanyMembershipPlan(empresaId, 'free', { membershipRenewsAt: '' });
      }

      notifyPlanDowngrade();

      setShowCancelModal(false);
      setCancelStep(0);
      setCancelReason('');
      setCancelFeedback('');

      if (typeof onClose === 'function') {
        onClose();
      }
      navigate('/dashboard?section=suscripciones');
    } catch (error) {
      setCancelError(
        error?.message ||
          'No pudimos registrar tu cancelación. Inténtalo nuevamente en unos segundos.'
      );
    } finally {
      setCancelSaving(false);
    }
  }, [
    activePlan?.id,
    cancelFeedback,
    cancelReason,
    cancelSaving,
    cancellationContext,
    currentPlanId,
    empresaId,
    navigate,
    normalizedPlanId,
    notifyPlanDowngrade,
    onClose,
  ]);

  const faqEntries = [
    {
      q: '¿Puedo cambiar de plan en cualquier momento?',
      a: 'Sí. El cambio se prorratea en tu siguiente ciclo y los beneficios se activan al instante.',
    },
    {
      q: '¿Qué pasa si se agotan mis créditos?',
      a: 'Puedes comprar paquetes adicionales de PPC o leads sin necesidad de cambiar de plan.',
    },
    {
      q: '¿Ofrecen planes Enterprise?',
      a: 'Sí, diseñamos propuestas a medida para operaciones de alto volumen. Escríbenos y te guiamos.',
    },
  ];

  const renderShell = body => {
    const overlayWrapper = isModal
      ? ['subscriptions-overlay', isEntering ? 'subscriptions-overlay--enter' : '']
          .filter(Boolean)
          .join(' ')
      : undefined;
    const shellClass = [
      'subscriptions-shell',
      isModal ? 'subscriptions-shell--overlay' : 'subscriptions-shell--page',
      isEntering ? 'subscriptions-shell--enter' : '',
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <div className={overlayWrapper}>
        <div className={shellClass}>{body}</div>
      </div>
    );
  };

  const renderRetentionModal = () => {
    if (!showCancelModal) return null;
    const endDateLabel = renewalLabel === 'Sin fecha' ? 'tu próximo ciclo' : renewalLabel;

    let stepBody = null;
    if (cancelStep === 0) {
      stepBody = (
        <>
          <p className="retention-losses__intro">
            Perderás acceso a estas herramientas exclusivas el {endDateLabel}.
          </p>
          <div className="retention-losses retention-losses--muted" role="list">
            {retentionLosses.map(loss => {
              const Icon = loss.icon;
              return (
                <article key={loss.key} className="retention-loss retention-loss--muted" role="listitem">
                  <span className="retention-loss__icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <div className="retention-loss__copy">
                    <strong>{loss.title}</strong>
                    <p>{loss.description}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </>
      );
    } else if (cancelStep === 1) {
      stepBody = (
        <form className="retention-survey" aria-label="Motivo de cancelación">
          <div className="retention-survey__grid">
            {CANCELLATION_REASONS.map(reason => {
              const isSelected = cancelReason === reason.value;
              return (
                <button
                  key={reason.value}
                  type="button"
                  className={`retention-survey__tile${isSelected ? ' is-selected' : ''}`}
                  onClick={() => setCancelReason(reason.value)}
                  aria-pressed={isSelected}
                >
                  <span className="retention-survey__indicator" aria-hidden="true" />
                  <span className="retention-survey__label">{reason.label}</span>
                </button>
              );
            })}
          </div>
          <label className="retention-feedback" htmlFor="retention-feedback-textarea">
            <span>¿Algo más que quieras contarnos? (Opcional)</span>
            <textarea
              id="retention-feedback-textarea"
              placeholder="Comparte cualquier contexto adicional..."
              value={cancelFeedback}
              onChange={event => setCancelFeedback(event.target.value)}
            />
          </label>
        </form>
      );
    } else {
      stepBody = (
        <p className="retention-modal__subtitle">
          Tu suscripción se cancelará y no se te volverá a cobrar. Tu acceso termina el {endDateLabel}.
        </p>
      );
    }

    return (
      <div className="retention-modal-overlay">
        <div
          className="retention-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="retention-modal-title"
        >
          <button
            type="button"
            className="retention-modal__close"
            onClick={handleCloseCancelModal}
            aria-label="Cerrar"
          >
            <FiX aria-hidden="true" />
          </button>
          <span className="retention-step-pill">Paso {cancelStep + 1} de 3</span>
          <h3 id="retention-modal-title">{RETENTION_STEP_TITLES[cancelStep]}</h3>
          <div className="retention-modal__content">{stepBody}</div>
          {cancelError ? <p className="retention-error">{cancelError}</p> : null}
          <div
            className={
              cancelStep === 0 ? 'retention-actions retention-actions--stack' : 'retention-actions'
            }
          >
            {cancelStep === 0 && (
              <>
                <div className="retention-primary-group">
                  <span className="retention-primary-badge">Recomendado</span>
                  <button type="button" className="retention-hero-btn" onClick={handleCloseCancelModal}>
                    Mantener mi plan
                  </button>
                </div>
                <button
                  type="button"
                  className="retention-secondary-link"
                  onClick={handleAdvanceCancelStep}
                >
                  Continuar con la cancelación
                </button>
              </>
            )}
            {cancelStep === 1 && (
              <>
                <button
                  type="button"
                  className="retention-btn retention-btn--primary"
                  onClick={handleAdvanceCancelStep}
                  disabled={!cancelReason}
                >
                  Continuar
                </button>
                <button
                  type="button"
                  className="retention-btn retention-btn--ghost"
                  onClick={handleBackCancelStep}
                >
                  Volver
                </button>
              </>
            )}
            {cancelStep === 2 && (
              <>
                <button
                  type="button"
                  className="retention-btn retention-btn--danger"
                  onClick={handleConfirmCancel}
                  disabled={cancelSaving}
                >
                  Sí, cancelar suscripción
                </button>
                <button
                  type="button"
                  className="retention-btn retention-btn--ghost"
                  onClick={handleCloseCancelModal}
                >
                  No, me quedo
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return renderShell(
      <>
        <header className="subscriptions-header">
          <div className="subscriptions-loading-title">
            <span className="subscriptions-skeleton eyebrow" />
            <span className="subscriptions-skeleton heading" />
            <span className="subscriptions-skeleton paragraph" />
          </div>
        </header>
        <div className="subscriptions-loading-grid">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="subscriptions-loading-card">
              <span className="subscriptions-skeleton title" />
              <span className="subscriptions-skeleton line" />
              <span className="subscriptions-skeleton line" />
            </div>
          ))}
        </div>
      </>
    );
  }

  if (error) {
    return renderShell(
      <section className="subscriptions-error">
        <h2>No fue posible cargar los planes</h2>
        <p>{error}</p>
      </section>
    );
  }

  if (!displayPlans.length && isModal) {
    return renderShell(
      <section className="subscriptions-empty">
        <h2>No hay planes configurados</h2>
        <p>
          Aun no se han registrado planes de membresia. Ingresa al panel de administracion o
          contacta a soporte para configurarlos.
        </p>
      </section>
    );
  }

  if (!isModal) {
    return renderShell(
      <section
        className="membership-dashboard"
        aria-label="Panel de membresia Flotte"
        style={membershipThemeStyles}
      >
        <div className="membership-wallet-grid">
          <div className="membership-card-column">
            <div className="membership-hero-card" style={heroCardStyles}>
              <div className="membership-hero-card__top">
                <span className="membership-hero-card__logo">FLOTTE</span>
                <div className="membership-hero-card__actions">
                  <span className="membership-hero-card__badge">{heroBadgeLabel}</span>
                  <button
                    type="button"
                    className="membership-hero-card__manage"
                    onClick={handleManageSubscription}
                    aria-label="Administrar suscripción"
                  >
                    <FiArrowUpRight aria-hidden="true" />
                  </button>
                </div>
              </div>
              <div className="membership-hero-card__middle" aria-hidden="true" />
              <div className="membership-hero-card__bottom">
                <h1>{empresaName || 'Tu empresa'}</h1>
                {showMemberSince ? (
                  <span className="membership-hero-card__since">Member since {memberSinceYear}</span>
                ) : null}
              </div>
            </div>
          </div>
          <div className="membership-wallet-stats" role="list">
            {statCards.map(stat => {
              const Icon = stat.icon || FiTrendingUp;
              return (
                <article
                  key={stat.key}
                  className="wallet-stat-card wallet-stat-card--minimal"
                  role="listitem"
                >
                  <span className="wallet-stat-card__icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <div className="wallet-stat-card__content">
                    <div className="wallet-stat-card__value-row">
                      <strong className="wallet-stat-card__value">{stat.value}</strong>
                      {stat.badge ? (
                        <span className={`wallet-pill wallet-pill--${stat.badgeTone || 'muted'}`}>
                          {stat.badge}
                        </span>
                      ) : null}
                    </div>
                    <p className="wallet-stat-card__label">{stat.label}</p>
                    {stat.helper ? <small>{stat.helper}</small> : null}
                    {stat.cancelAction ? (
                      <button
                        type="button"
                        className="retention-trigger"
                        onClick={handleOpenCancelModal}
                      >
                        Cancelar suscripción
                      </button>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        <section className="membership-benefits" aria-label="Beneficios activos">
          <header>
            <span className="section-eyebrow">Tus Beneficios Elite</span>
          </header>
          <div className="membership-benefits-grid">
            {powerUps.map((card, index) => {
              const Icon = card.icon || FiShield;
              const tone = benefitTone(card.title);
              return (
                <article key={`${card.key || 'powerup'}-${index}`} className="benefit-card">
                  <span className={`benefit-card__icon benefit-card__icon--${tone}`}>
                    <Icon aria-hidden="true" />
                  </span>
                  <div>
                    <strong>{card.title}</strong>
                    <p>{card.description}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="membership-upsell membership-upsell--slim" aria-label="Escala a enterprise">
          <div>
            <span className="section-eyebrow">Enterprise</span>
            <h3>Sube de nivel tu membresía</h3>
            <p>Más créditos, integraciones y soporte dedicado cuando lo necesites.</p>
          </div>
          <button
            type="button"
            className="membership-upsell__action"
            onClick={handleContactSales}
          >
            Habla con Flotte
          </button>
        </section>

        {renderRetentionModal()}
      </section>
    );
  }

  return renderShell(
    <>
      <button
        type="button"
        className="subscriptions-overlay-close"
        onClick={() => onClose?.()}
        aria-label="Cerrar"
      >
        x
      </button>

      <header className="subscriptions-header subscriptions-header--overlay">
        <div>
          <span className="subscriptions-eyebrow">{modalCopy?.ribbon || 'Plan actual'}</span>
          <h1>{modalCopy?.title || 'Actualiza tu plan'}</h1>
          <p>
            {modalCopy?.subtitle ||
              'Elige el plan que mejor se adapte a tus operaciones y empieza a crecer.'}
          </p>
        </div>
        <div
          className="subscriptions-toggle"
          role="group"
          aria-label="Seleccionar periodo de facturacion"
        >
          <button
            type="button"
            onClick={() => setBillingCycle('monthly')}
            className={billingCycle === 'monthly' ? 'active' : ''}
          >
            Mensual
          </button>
          <button
            type="button"
            onClick={() => setBillingCycle('yearly')}
            className={billingCycle === 'yearly' ? 'active' : ''}
          >
            Anual
            <span className="subscriptions-toggle__badge">Ahorra 15%</span>
          </button>
        </div>
      </header>

      <section className="subscriptions-grid subscriptions-grid--pricing" aria-label="Opciones de mejora">
        {planGroups.map((group, index) => (
          <div
            key={`group-${index}`}
            className={`subscriptions-row ${
              isModal
                ? 'subscriptions-row--pricing'
                : group.length === 2
                ? 'subscriptions-row--two'
                : 'subscriptions-row--three'
            }`}
          >
            {group.map((plan, planIndex) => {
              const isCurrent = plan.slug === normalizedPlanId;
              const isRecommended = plan.slug === 'premium';
              const pricePeriod = plan.priceLabel?.includes('ano') ? '/año' : '/mes';
              const isPremiumTier = ['premium', 'elite'].includes(plan.slug);
              const featureList = [
                {
                  key: 'publications',
                  icon: FiCheck,
                  content: (
                    <>
                      <strong>{plan.publicationsLimit}</strong> Publicaciones activas
                    </>
                  ),
                },
                {
                  key: 'team',
                  icon: FiCheck,
                  content: (
                    <>
                      <strong>{plan.teamSeats || 'Ilimitados'}</strong> Usuarios de equipo
                    </>
                  ),
                },
                {
                  key: 'credits',
                  icon: FiCheck,
                  content: (
                    <>
                      <strong>{plan.ppcCredits || 0} PPC</strong> / <strong>{plan.leadCredits || 0} Leads</strong> incluidos
                    </>
                  ),
                },
                {
                  key: 'verification',
                  icon: plan.verificationIncluded ? FiStar : FiShield,
                  tone: plan.verificationIncluded ? 'accent' : 'default',
                  content: plan.verificationIncluded ? (
                    <>
                      Verificación <strong>incluida</strong>
                    </>
                  ) : (
                    <>Verificación opcional</>
                  ),
                },
                ...(plan.features?.map((feature, extraIndex) => ({
                  key: `extra-${extraIndex}`,
                  icon: FiCheck,
                  label: feature,
                })) || []),
              ];
              const actionClass = [
                'pricing-card__action',
                isPremiumTier ? 'pricing-card__action--primary' : 'pricing-card__action--outline',
                isCurrent ? 'pricing-card__action--current' : '',
              ]
                .filter(Boolean)
                .join(' ');
              const cardClass = ['pricing-card', isRecommended ? 'pricing-card--featured' : '', isCurrent ? 'pricing-card--current' : '']
                .filter(Boolean)
                .join(' ');

              return (
                <article key={plan.slug} className={cardClass}>
                  {isRecommended ? <span className="pricing-card__tag">Recomendado</span> : null}
                  <header className="pricing-card__head pricing-card__head--stack">
                    <h2>{plan.name}</h2>
                    <div className="pricing-card__price">
                      <span>{plan.displayPrice}</span>
                      <small>{pricePeriod}</small>
                    </div>
                    <p>{plan.description || ''}</p>
                  </header>

                  <ul className="pricing-card__features">
                    {featureList.map(item => {
                      const IconComponent = item.icon || FiCheck;
                      const content = item.content || item.label;
                      return (
                        <li key={item.key} className="pricing-card__feature">
                          <span className="pricing-card__feature-icon" data-tone={item.tone || 'default'}>
                            <IconComponent aria-hidden="true" />
                          </span>
                          <span className="pricing-card__feature-text">{content}</span>
                        </li>
                      );
                    })}
                  </ul>

                  <button
                    type="button"
                    className={actionClass}
                    onClick={() => handlePlanAction(plan)}
                    disabled={isCurrent}
                  >
                    {isCurrent ? 'Plan actual' : 'Solicitar este plan'}
                  </button>
                </article>
              );
            })}
          </div>
        ))}
      </section>

      <footer className="subscriptions-faq" aria-label="Preguntas frecuentes">
        <h2>Preguntas frecuentes</h2>
        <div className="subscriptions-faq__items">
          {faqEntries.map(entry => (
            <article key={entry.q} className="subscriptions-faq__item">
              <strong>{entry.q}</strong>
              <p>{entry.a}</p>
            </article>
          ))}
        </div>
      </footer>
    </>
  );
}
